import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddImage from '../../../../assets/img/add.svg';
import Icon from '../../../../ui/Icon';
import Button from 'src/ui/Button';
import acceptedImageFormats from 'src/common/constants/imgFormats';
import { useAppDispatch, useAppSelector } from 'src/hooks';

// Interfaces
import { TherapistGeneralFormProps } from '../../../../interfaces/therapyInterface';
import { TherapyProfilePictureFormProps } from 'src/interfaces/therapyInterface';

// Services
import ConfirmationPopup from 'src/ui/popups/ConfirmationPopup';
import { setEditTherapistDetailView, setTherapistDetailView } from 'src/redux/slices/therapy.slice';
import PopUp from 'src/ui/Popup';
import { deleteTherapyProfilePictureUseCase } from 'src/services/therapy/useCases';

//images
import rightSvg from '../../../../assets/img/round-right.svg';

const TherapyAdditionalInfo = (props: TherapistGeneralFormProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation(['addEditUserProfilePictureForm']);

  const { onSuccess, data, isEditing } = props;

  const image = new Image();
  const fileInputRef = React.createRef<HTMLInputElement>();

  const currentRole = useAppSelector((state) => state.auth.role);
  const currentTherapistId = JSON.parse(localStorage.getItem('current-Therapist') || '');
  const { TherapistDetailView, editTherapistDetailView } = useAppSelector((state) => state.therapy);

  const [preview, setPreview] = useState<any>(data?.profilePicture || '');
  const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
  const [showDeleteProfilePicture, setShowDeleteProfilePicture] = useState(false);
  const [profilePicture, setProfilePicture] = useState<any>();

  useEffect(() => {
    dispatch(setEditTherapistDetailView(''));
    if (TherapistDetailView === 'finish' || editTherapistDetailView === 'finish') {
      setShowSuccessPopup(true);
    }
  }, [TherapistDetailView, dispatch, editTherapistDetailView]);

  const formik = useFormik<TherapyProfilePictureFormProps>({
    initialValues: {
      profilePicture: '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      profilePicture: Yup.mixed()
        .notRequired()
        .test('type', t('errorImageFormats'), (file) => {
          if (file) {
            return acceptedImageFormats.includes(file.type);
          }
          return true;
        })
        .test('fileSize', t('errorFileSize'), (file) => {
          if (file) {
            return file.size <= 10485760;
          }
          return true;
        })
        .test('imageWidth', t('errorWidth'), (file) => {
          if (file && image.width > 2048) {
            return false;
          }
          return true;
        })
        .test('imageHeight', t('errorHeight'), (file) => {
          if (file && image.height > 2048) {
            return false;
          }
          return true;
        }),
    }),
    onSubmit: (values) => {
      onSuccess(values);
    },
  });

  const handlePreviewProfile = () => {
    setProfilePicture(null);
    formik.setFieldValue('profilePicture', null);
    setPreview('');
  };

  const handleNavigation = () => {
    // dispatch(setTherapistDetail(''));
    dispatch(setEditTherapistDetailView(''));
    if (currentRole === 'admin') {
      navigate(`/admin/therapy/${currentTherapistId}/edit-therapy`);
    } else if (currentRole === 'therapist') {
      navigate(`/therapist/${currentTherapistId}/edit-therapy`);
    }
  };

  const handleDelete = () => {
    handlePreviewProfile();
    if (profilePicture?.inServer) {
      dispatch(deleteTherapyProfilePictureUseCase(currentTherapistId));
    }
    setShowDeleteProfilePicture(false);
  };

  useEffect(() => {
    if (data?.profilePicture) {
      const r: any = Object.assign({}, data?.profilePicture);
      r.urlPreview = data?.profilePicture;
      r.inServer = true;
      setProfilePicture(r);
      setPreview(r.urlPreview);
    }
  }, [data?.profilePicture]);

  const CloseHandler = () => {
    setShowSuccessPopup(false);
    if (currentRole === 'admin') {
      dispatch(setTherapistDetailView('additional-info'));
      navigate('/admin/therapy');
    } else navigate('/therapist/profile');
  };

  return (
    <div
      className='relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-2xl bg-white text-primary overflow-auto'
      style={{ maxHeight: '75vh' }}
    >
      <div className='flex items-center p-3 bg-background justify-between'>
        <div className='flex items-center'>
          {isEditing && <Icon icon='backBtn' onClick={handleNavigation} />}
          <span className='flex font-semibold text-sm mx-2 uppercase'>
            {isEditing ? 'edit therapist profile picture' : 'add therapist profile picture'}
          </span>
        </div>
        <Button
          extraClass='flex font-semibold !text-black !border-0 mx-2 uppercase !w-8 !bg-background !shadow-none'
          onClick={() => {
            if (isEditing) {
              dispatch(setEditTherapistDetailView('finish'));
            } else {
              dispatch(setTherapistDetailView('finish'));
            }
          }}
        >
          skip
        </Button>
      </div>
      <div className='formContainer' style={{ height: '75vh' }}>
        <form
          onSubmit={formik.handleSubmit}
          className='profilePictureForm pt-4 items-center'
          style={{ height: '75vh' }}
        >
          <div className='flex justify-center pt-5 relative'>
            <div className='w-64'>
              <div className='formUsersHeader text-2xl text-center mt-8'>
                {isEditing ? data?.fullName : 'Profile picture'}
              </div>
              {formik.touched.profilePicture && formik.errors.profilePicture && (
                <div className='formUsersError mb-4'>{formik.errors.profilePicture}</div>
              )}
              {preview ? (
                <div className='relative'>
                  <img
                    src={preview}
                    alt='preview'
                    className='profile-picture'
                    //onClick={handlePreviewProfile}
                    aria-hidden='true'
                  />
                  <Icon
                    icon='delete'
                    extraClass='delete-profile-picture'
                    onClick={() => setShowDeleteProfilePicture(true)}
                  />
                </div>
              ) : (
                <button
                  type='button'
                  className='non-profile-picture bg-background'
                  onClick={(e) => {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }}
                >
                  <img src={AddImage} alt='add' />
                </button>
              )}
              <input
                ref={fileInputRef}
                className='hidden'
                type='file'
                name='file'
                accept='image/*'
                onBlur={formik.handleBlur}
                onChange={(event) => {
                  formik.setFieldValue(
                    'profilePicture',
                    event.currentTarget.files ? event.currentTarget.files[0] : 'no file',
                  );
                  if (
                    event.target.files &&
                    event.target.files[0].type.substring(0, 5) === 'image'
                  ) {
                    const iFile = {
                      id: 1,
                      inServer: false,
                      file: event.target.files[0],
                      urlPreview: URL.createObjectURL(event.target.files[0]),
                    };
                    setProfilePicture(iFile);
                    setPreview(iFile.urlPreview);
                  }
                }}
              />
              <div className='flex justify-center mt-8'>
                <Button type='submit' extraClass='py-3.5 px-5 font-medium font-semibold capitalize'>
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <ConfirmationPopup
        isVisible={showDeleteProfilePicture}
        title={t('deleteAlert.title')}
        subtitle={t('deleteAlert.subtitle')}
        submitBtn={t('deleteAlert.confirm')}
        handleSubmit={handleDelete}
        onClose={() => setShowDeleteProfilePicture(false)}
      />

      <PopUp
        isVisible={showSuccessPopup}
        extraClassesChildren='flex flex-col items-center justify-center'
        onClose={() => CloseHandler()}
      >
        <div className='flex flex-col items-center justify-content '>
          <img className='m-4' src={rightSvg} alt='right' />
          <span className='font-sm text-4xl text-black-500'>Therapist added</span>
          <button
            className='font-bold text-md text-white bg-purple-600 w-48 h-12 my-8 font'
            style={{
              borderRadius: '20px',
            }}
            onClick={() => CloseHandler()}
          >
            OK
          </button>
        </div>
      </PopUp>
    </div>
  );
};

export default TherapyAdditionalInfo;
